import React, { useState, useEffect } from 'react';
import { Input, InputAdornment } from 'tea-component';
import { mathFormat } from '@sg/utils/common';

interface IInputNumberComponentProps {
  value: any;
  onChange: (newValue: any) => void;
  record: any;
  width?: string;
  cutoffYear?: number; // Year from lifecycleUpdateTime (datatime). Records with year > cutoffYear are editable.
}

/**
 * InputNumberComponent - A component for editing financial values in table cells
 * 
 * Features:
 * - Converts values between display format (millions) and stored format (actual values)
 * - Validates input to only allow numbers, decimals, and negative signs
 * - Conditionally renders: editable input for future years, read-only display for past years
 * - Uses cutoffYear (from API datatime) instead of current calendar year to determine editability
 * - Handles empty values and invalid inputs gracefully
 */
const InputNumberComponent: React.FC<IInputNumberComponentProps> = ({ 
  value, 
  onChange, 
  record, 
  width = '55px',
  cutoffYear,
}) => {
  // Display value: actual value / 1,000,000 (convert to M unit)
  const [inputValue, setInputValue] = useState(
    value !== undefined && value !== null && value !== '' 
      ? String(value / 1000000) 
      : ''
  );
  // Use cutoffYear from API datatime if provided, otherwise fall back to current year
  const baseYear = cutoffYear ?? new Date().getFullYear();
  const recordYear = parseInt(record?.date || '0');
  
  useEffect(() => {
    setInputValue(
      value !== undefined && value !== null && value !== '' 
        ? String(value / 1000000) 
        : ''
    );
  }, [value]);
  
  const handleChange = (newValue: any) => {
    // Only allow numbers, decimal points, and negative signs
    let sanitizedValue = newValue.replace(/[^0-9.-]/g, '');
    
    // Ensure negative sign is only at the beginning
    const hasNegativeAtStart = sanitizedValue.startsWith('-');
    sanitizedValue = sanitizedValue.replace(/-/g, '');
    if (hasNegativeAtStart) {
      sanitizedValue = `-${sanitizedValue}`;
    }
    
    // Ensure only one decimal point
    const dotIndex = sanitizedValue.indexOf('.');
    if (dotIndex !== -1) {
      sanitizedValue = sanitizedValue.substring(0, dotIndex + 1) + 
        sanitizedValue.substring(dotIndex + 1).replace(/\./g, '');
    }
    
    setInputValue(sanitizedValue);
    
    // Handle empty values: return empty string
    if (sanitizedValue === '' || sanitizedValue === null || sanitizedValue === undefined || sanitizedValue === '-') {
      onChange('');
      return;
    }
    
    // Convert back to actual value: input value * 1,000,000
    const parsedValue = parseFloat(sanitizedValue);
    
    // If parsing fails, return empty string
    if (isNaN(parsedValue)) {
      onChange('');
      return;
    }
    
    // If valid number (including 0), return number type
    const actualValue = parsedValue * 1000000;
    onChange(actualValue); // Number type
  };
  
  // If year is greater than the cutoff year (API datatime), show input field; otherwise show formatted data
  if (recordYear > baseYear) {
    return (
      <div className="dbs-input-number-component">
        <InputAdornment after="M">
          <Input 
            key={record?.id} 
            value={inputValue} 
            onChange={handleChange} 
            style={{
              textAlign: 'right',
            //   minWidth: '40px', // Minimum width to prevent shrinking
            //   width: '100%'
            }} 
            placeholder="Data"
          />
        </InputAdornment>
      </div>
    );
  } 
  
  return (
    <div style={{textAlign: 'right', width, textWrap: 'nowrap'}}>
      {mathFormat(value)}
    </div>
  );
};

export default InputNumberComponent;

